import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** The severe-failure discriminator, mirroring the register/recover flows. */
export type ChangePasswordStage = 'preCommit' | 'postCommit';

/** Ordered phases the change-password workflow moves through. */
export type ChangePasswordPhase =
  | 'idle'
  | 'preparing'
  | 'regenerating'
  | 'awaitingConfirmation'
  | 'bundling'
  | 'uploading'
  | 'submitting'
  | 'committing'
  | 'complete'
  | 'error';

export interface ChangePasswordState {
  phase: ChangePasswordPhase;
  regeneratedCount: number;
  totalCount: number;
  failedFileIds: string[];
  error?: string;
  stage?: ChangePasswordStage;
}

const initialState: ChangePasswordState = {
  phase: 'idle',
  regeneratedCount: 0,
  totalCount: 0,
  failedFileIds: [],
};

const changePasswordSlice = createSlice({
  name: 'changePassword',
  initialState,
  reducers: {
    changePasswordStarted() {
      return { ...initialState, phase: 'preparing' as const };
    },
    phaseChanged(state, action: PayloadAction<ChangePasswordPhase>) {
      state.phase = action.payload;
    },
    regenerationStarted(state, action: PayloadAction<{ totalCount: number }>) {
      state.phase = 'regenerating';
      state.totalCount = action.payload.totalCount;
      state.regeneratedCount = 0;
    },
    fileKeyRegenerated(state) {
      state.regeneratedCount += 1;
    },
    fileKeyRegenerationFailed(state, action: PayloadAction<string>) {
      state.failedFileIds.push(action.payload);
    },
    /**
     * Some keys failed to regenerate; the flow paused before anything was
     * committed and needs the user's explicit go-ahead to rotate without them.
     */
    confirmationRequired(state) {
      state.phase = 'awaitingConfirmation';
    },
    changePasswordCompleted(state) {
      state.phase = 'complete';
    },
    changePasswordFailed(
      state,
      action: PayloadAction<{ message: string; stage?: ChangePasswordStage }>
    ) {
      state.phase = 'error';
      state.error = action.payload.message;
      state.stage = action.payload.stage;
    },
    resetChangePassword() {
      return initialState;
    },
  },
});

export const {
  changePasswordStarted,
  phaseChanged,
  regenerationStarted,
  fileKeyRegenerated,
  fileKeyRegenerationFailed,
  confirmationRequired,
  changePasswordCompleted,
  changePasswordFailed,
  resetChangePassword,
} = changePasswordSlice.actions;

export default changePasswordSlice.reducer;
